import { type SugarPublishedTuitionRate } from '@app/types'
import { ActionIcon, Card, Collapse, Group, Table, Text, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { type FC } from 'react'

import { formatCurrency } from '@app/lib'

export interface TuitionRatesHistoryPanelProps {
  history: SugarPublishedTuitionRate[]
  currentRecordId?: string
}

export const TuitionRatesHistoryPanel: FC<TuitionRatesHistoryPanelProps> = ({ history, currentRecordId }) => {
  const [opened, { toggle }] = useDisclosure(false)

  // Recompute on every render so newly-past effective dates appear after
  // midnight on the next render/interaction without requiring a refetch.
  const today = dayjs()
  const previous = history.filter(
    (record) => record.id !== currentRecordId && dayjs(record.effectiveDate).isBefore(today, 'day')
  )

  if (previous.length === 0) return null

  return (
    <Card
      mt='lg'
      radius='lg'
      withBorder
    >
      <Group position='apart'>
        <Title order={5}>Previous Rates ({previous.length})</Title>
        <ActionIcon
          aria-label={opened ? 'Collapse previous rates' : 'Expand previous rates'}
          onClick={toggle}
          variant='subtle'
        >
          {opened ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
        </ActionIcon>
      </Group>
      <Collapse in={opened}>
        <Table mt='md'>
          <thead>
            <tr>
              <th>Effective Date</th>
              <th>4-Year-Old</th>
              <th>3-Year-Old</th>
              <th>DPP Scholarship</th>
            </tr>
          </thead>
          <tbody>
            {previous.map((record) => (
              <tr key={record.id}>
                <td>
                  <Text size='sm'>{dayjs(record.effectiveDate).format('MMM D, YYYY')}</Text>
                </td>
                <td>
                  <Text size='sm'>{formatCurrency(record.fourYearOld.maxMonthlyRate)}</Text>
                </td>
                <td>
                  {record.ratesDifferThreeYr && record.threeYearOld ? (
                    <Text size='sm'>{formatCurrency(record.threeYearOld.maxMonthlyRate)}</Text>
                  ) : (
                    <Text
                      color='dimmed'
                      size='sm'
                    >
                      Same as 4-Year-Old
                    </Text>
                  )}
                </td>
                <td>
                  {record.scholarshipOptIn === true ? (
                    <Text size='sm'>Opted In</Text>
                  ) : record.scholarshipOptIn === false ? (
                    <Text size='sm'>Opted Out</Text>
                  ) : (
                    <Text
                      color='dimmed'
                      size='sm'
                    >
                      Not selected
                    </Text>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Collapse>
    </Card>
  )
}
